import { useMemo } from "react";
import { Button, styled, Tooltip } from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router";
import { generatePath } from "react-router-dom";

import TableComponent from "../../components/TableView/TableComponent";
import { TableSchema } from "../../components/TableView";
import { Text } from "../../components/Typography";
import { PulseCircle } from "../../components";
import { AppRoutes, landDetailLink } from "../../config";
import { LandToken } from "../../types/LandToken";
import { baseLandsSchema, trustCol } from "../../utils/table-schemas";
import { DATE_FORMAT } from "../../utils/constants";
import { EmptyBox } from "./Guarding";

/**
 * Which watchdog queue a row came from. The two are disjoint by construction — a review row is
 * APPROVED and unminted, an attest row is DEED_ANCHORED after a sale — so tagging at merge time
 * is safe and beats re-deriving the stage from the token's shape in every cell.
 */
type QueueStage = "review" | "attest";
type QueueRow = LandToken & { stage: QueueStage };

/**
 * The Review tab of the trust portal: the independent watchdog duty, at both lifecycle stages
 * it applies to.
 *
 *  - Score review (GET /trust/tokens/review): verifications submitted by another org's verifier
 *    that this wallet may challenge before the guardian mints. The only action is the negative
 *    one — nothing needs doing unless the assessment is wrong — and it is raised on the land's
 *    verification page, read-only, where the record under review is (the same flow as the
 *    post-mint covenant challenge). The page sends them back here once it lands.
 *  - Deed attestation (GET /trust/tokens/attestation): deeds anchored by another org's verifier
 *    that this wallet may independently attest. Attesting happens on the land page —
 *    SettlementPanel carries the gasless attest/challenge actions — so the row only links there.
 *
 * Both lists are rendered as-is — no client-side filtering. The backend already excluded
 * everything the contract would reject (already minted / challenged / attested, no requestId,
 * wrong country, or scored inside the caller's own on-chain org), so filtering again here could
 * only ever hide a row that is genuinely actionable.
 *
 * The two queues have different middle columns (a score vs a deed date, a submission date vs a
 * deadline), so they are folded into one "Stage" and one "Submitted / Due" column rather than
 * showing four columns that are each blank for half the rows.
 */
const ReviewLands = ({
  reviewLands,
  attestLands,
  isLoading,
}: {
  reviewLands: LandToken[];
  attestLands: LandToken[];
  isLoading: boolean;
}) => {
  const navigate = useNavigate();

  // Score reviews first: they come one stage earlier, and a bad score blocks a mint whereas an
  // attestation only delays a settlement that already has a deadline of its own.
  const rows = useMemo<QueueRow[]>(
    () => [
      ...reviewLands.map((land) => ({ ...land, stage: "review" as const })),
      ...attestLands.map((land) => ({ ...land, stage: "attest" as const })),
    ],
    [reviewLands, attestLands]
  );

  const reviewSchema: TableSchema<QueueRow>[] = [
    ...(baseLandsSchema as TableSchema<QueueRow>[]),
    // The verifier whose score / deed you are independently checking — the whole point of the
    // independence rule, so it belongs in the row.
    trustCol as TableSchema<QueueRow>,
    {
      id: "stage",
      label: "Stage",
      sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
      render: (_data) => {
        if (_data.stage === "review") {
          // The submitted assessment is what is being disputed, so show the number rather than
          // making the reviewer open the land to find it. Highest id is the latest verification,
          // matching how the rest of the app picks one.
          const latest = (_data.verifications ?? []).reduce(
            (acc: any, row: any) => (!acc || row?.id > acc?.id ? row : acc),
            null as any
          );
          const score = latest?.ecoScore;
          return (
            <Text mono sx={{ flexDirection: "column" }}>
              <Text medium contrast>
                Score review
              </Text>
              <Text sx={{ color: "text.disabled" }}>
                EcoScore {score != null ? (Number(score) * 100).toFixed(2) : "—"}
              </Text>
            </Text>
          );
        }
        return (
          <Text mono sx={{ flexDirection: "column" }}>
            <Text medium contrast>
              Deed attestation
            </Text>
            <Text sx={{ color: "text.disabled" }}>
              Deed {_data.etNft?.deedDate ? moment(_data.etNft.deedDate).format(DATE_FORMAT) : "—"}
            </Text>
          </Text>
        );
      },
    },
    {
      id: "when",
      label: "Submitted / Due",
      sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
      render: (_data) => {
        if (_data.stage === "review") {
          // lastVerified is when the assessment landed on chain — how long the guardian has been
          // able to mint it, which is what makes one row more urgent to review than another.
          const submitted = _data.lastVerified ?? _data.timestamps?.verifiedAt ?? null;
          if (!submitted) {
            return (
              <Text medium mono sx={{ color: "text.disabled" }}>
                —
              </Text>
            );
          }
          const at = moment(submitted);
          return (
            <Tooltip arrow placement="bottom" title={`Submitted ${at.fromNow()}`}>
              <span style={{ cursor: "pointer" }}>
                <Text mono sx={{ flexDirection: "column" }}>
                  {at.format(DATE_FORMAT)}
                  <Text sx={{ color: "text.disabled" }}>{at.fromNow(true)} ago</Text>
                </Text>
              </span>
            </Tooltip>
          );
        }

        // deadlineAt is derived server-side (SettlementService.deriveNextStep) — for a
        // deed_anchored row it is withholdUntil. Never recompute it here: the window is
        // backend config the client cannot see, and it has already been retuned once.
        const deadline = _data.etNft?.deadlineAt ?? _data.etNft?.withholdUntil ?? null;
        if (!deadline) {
          return (
            <Text medium mono sx={{ color: "text.disabled" }}>
              —
            </Text>
          );
        }

        const due = moment(deadline);
        // Past the withhold window a holder can step in as backstop attestor and the server
        // may finalise, so an overdue row is genuinely more urgent, not merely late.
        const pastDue = due.isBefore();
        const soon = !pastDue && due.isBefore(moment().add(1, "h"));

        return (
          <Tooltip
            arrow
            placement="bottom"
            title={pastDue ? `Withhold window closed ${due.toNow(true)} ago` : `Closes ${due.fromNow()}`}
          >
            <span style={{ cursor: "pointer" }}>
              <Text
                mono
                sx={{
                  flexDirection: "column",
                  color: pastDue ? "error.main" : soon ? "action.pulse" : "text.primary",
                }}
              >
                <Text medium contrast sx={{ p: 0, display: "flex", alignItems: "center" }}>
                  {soon && <PulseCircle />}
                  {due.format(DATE_FORMAT)}
                </Text>
                <Text sx={{ color: "text.disabled" }}>{pastDue ? "overdue" : due.fromNow(true)}</Text>
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: "reviewActions",
      label: "Actions",
      sx: { width: "180px" },
      render: (_data) => {
        if (_data.stage === "review") {
          // The challenge is keyed on the requestId and there is no fallback — a row without one
          // cannot be challenged however eligible it otherwise looks. The backend already filters
          // these out; the guard is here because the button would otherwise send a malformed call.
          const requestId = _data.requestId;
          return (
            <div style={{ display: "flex" }}>
              <Tooltip title={requestId ? "" : "This verification has no on-chain request id"}>
                <span>
                  <TableActionButton
                    onClick={() =>
                      navigate(generatePath(AppRoutes.Verification.path, { tokenId: String(_data.id) }), {
                        // Back here (the Review tab) from the page's Back and once the challenge lands.
                        state: { ref: `${AppRoutes.Profile.subRoutes.Verification.path}?review` },
                      })
                    }
                    size="small"
                    variant="contained"
                    disabled={!requestId}
                    sx={{ textTransform: "capitalize", "&:hover": { border: "none", outline: "none" } }}
                  >
                    Challenge
                  </TableActionButton>
                </span>
              </Tooltip>
            </div>
          );
        }

        // A server-signed settlement tx is already in flight for this token — acting now would
        // just burn relay gas on a call the chain is about to make redundant.
        const txPending = !!_data.etNft?.settlementTxAction;
        return (
          <div style={{ display: "flex" }}>
            <Tooltip title={txPending ? "A settlement transaction is already in flight for this land" : ""}>
              <span>
                <TableActionButton
                  onClick={() => navigate(landDetailLink(_data.id))}
                  size="small"
                  variant="contained"
                  disabled={txPending}
                  sx={{ textTransform: "capitalize", "&:hover": { border: "none", outline: "none" } }}
                >
                  {txPending ? "Processing" : "Attest"}
                </TableActionButton>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <EmptyBox sx={{ borderRadius: "20px", marginTop: "0px", height: "57vh" }}>
        <span>🔎</span>
        <span>Data is loading...</span>
      </EmptyBox>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyBox sx={{ borderRadius: "20px", marginTop: "0px", height: "57vh" }}>
        <span>Nothing to review right now!</span>
        <span>Verifications you can challenge and deeds you can attest will appear here</span>
      </EmptyBox>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <TableComponent
        mobileTitle="Awaiting your review"
        schema={reviewSchema}
        customData={rows}
        apiURL=""
        title={" "}
        hideTitle={true}
        totalItems={rows.length}
        batch={5}
      />
    </div>
  );
};

// Every button in an Actions column takes one fixed height, so a cell that mixes text and
// icon-only buttons lines them up instead of letting each one's content set its size. The
// padding here is inner padding, horizontal only — the height is already fixed.
const TABLE_ACTION_HEIGHT = "36px";

const TableActionButton = styled(Button)({
  height: TABLE_ACTION_HEIGHT,
  minHeight: TABLE_ACTION_HEIGHT,
  padding: "0 16px",
  whiteSpace: "nowrap",
  backgroundColor: "black",
  color: "white",
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  border: "none",
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

export default ReviewLands;
